import { createSelector } from 'reselect';
import { every, forEach, concat, cloneDeep } from 'lodash';
import {
  getActiveAccounts,
  getBalances,
  getAccountsWithIntraday
} from 'state/account/AccountSelectors';
import { isCollegeSavings } from 'services/Products';
import {
  dailyChangeAmount,
  dailyChangeRate,
  maxAsOfDate,
  percentChange
} from 'services/Calculation';

export const getRpsBalances = (state) => state.rpsBalances;

export const getRpsYearEndBalance = (state) => state.rpsYearEndBalance;

const accountBalancesHelper = (balances) => {
  for (let key in balances.products.types) {
    balances.aggregate = 0.0;
    balances.products.types[key].balance = 0.0;
    balances.products.types[key].userAccountNumbers = [];
  }
};

/* This code was copied from AccountBalancesReducer.
 The purpose of this selector is to use accounts returned
 from the `getAccountsWithIntraday` to adjust balances if
 and when intraday brokerage accounts are present */
export const getAccountBalances = createSelector(
  getAccountsWithIntraday,
  getBalances,
  (accountsWithIntraday, oldBalances) => {
    const balances = cloneDeep(oldBalances);
    balances.aggregate = 0.0;
    balances.dailyChange = 0.0;
    accountBalancesHelper(balances.retirement);
    accountBalancesHelper(balances.nonRetirement);

    // filter
    const nonPurgedAccounts = accountsWithIntraday.filter(
      (a) => !a.purgedAccount
    );
    const visibleNonPurgedAccounts = nonPurgedAccounts.filter(
      (a) => !a.hiddenAccount
    );
    const hiddenAccounts = nonPurgedAccounts.filter(
      (account) => account.hiddenAccount
    );
    const zeroBalanceHiddenAccounts = hiddenAccounts.filter(
      (account) => account.balanceValue === 0.0
    );
    const accounts = concat(
      visibleNonPurgedAccounts,
      zeroBalanceHiddenAccounts
    );

    // populate
    let aggregatePrevious = 0.0;
    balances.asOfDate = maxAsOfDate(accounts);
    accounts.map((account) => {
      balances.aggregate += account.balanceValue;
      aggregatePrevious += account.previousBalanceValue;
      balances.dailyChange +=
        account.balanceValue - account.previousBalanceValue;
      if (account.retirementAccount) {
        let purpose = balances.retirement;
        purpose.aggregate += account.balanceValue;
        let product = purpose.products.byAccount[account.productType];
        if (product) {
          product.balance += account.balanceValue;
          product.userAccountNumbers.push(account.productType);
        }
      } else {
        let purpose = balances.nonRetirement;
        purpose.aggregate += account.balanceValue;
        let product = purpose.products.byAccount[account.productType];
        if (product) {
          product.balance += account.balanceValue;
          product.userAccountNumbers.push(account.productType);
        }
      }
    });

    balances.dailyRate = percentChange(balances.aggregate, aggregatePrevious);

    //TODO: Clean duplicate code by making modular functions
    const rp = balances.retirement;
    rp.percent = balances.aggregate ? rp.aggregate / balances.aggregate : 0.0;
    forEach(rp.products.types, function (type) {
      type.percent = rp.aggregate ? type.balance / rp.aggregate : 0.0;
    });
    const nrp = balances.nonRetirement;
    nrp.percent = balances.aggregate ? nrp.aggregate / balances.aggregate : 0.0;
    forEach(nrp.products.types, function (type) {
      type.percent = nrp.aggregate ? type.balance / nrp.aggregate : 0.0;
    });
    return balances;
  }
);

export const getAdvisoryBalances = createSelector(getBalances, (balances) => {
  const activeAdvisory = { balance: 0, userAccountNumbers: [] };
  const activePlus = { balance: 0, userAccountNumbers: [] };
  let balance = 0;
  let accountTypes = { activeAdvisory, activePlus };
  let accountNumbers = [];

  for (let key in balances.retirement.products.types) {
    if (key === 'activeAdvisory' || key === 'activePlus') {
      balance +=
        balances.retirement.products.types[key].balance +
        balances.nonRetirement.products.types[key].balance;
      accountTypes[key].balance +=
        balances.retirement.products.types[key].balance +
        balances.nonRetirement.products.types[key].balance;
      accountTypes[key].userAccountNumbers = [
        ...balances.retirement.products.types[key].userAccountNumbers,
        ...balances.nonRetirement.products.types[key].userAccountNumbers
      ];
      accountNumbers.push(...accountTypes[key].userAccountNumbers);
    }
  }

  const percents = {
    activeAdvisory:
      balance > 0 ? accountTypes['activeAdvisory'].balance / balance : 0,
    activePlus: balance > 0 ? accountTypes['activePlus'].balance / balance : 0
  };

  return {
    balance,
    accountTypes,
    percents,
    accountNumbers
  };
});

export const getSelfManagedBalances = createSelector(
  getBalances,
  (balances) => {
    const mutualFund = { balance: 0, userAccountNumbers: [] };
    const brokerage = { balance: 0, userAccountNumbers: [] };
    const collegeSavings = { balance: 0, userAccountNumbers: [] };
    let balance = 0;
    let accountTypes = { mutualFund, brokerage, collegeSavings };
    let accountNumbers = [];

    for (let key in balances.retirement.products.types) {
      if (
        key !== 'activeAdvisory' &&
        key !== 'activePlus' &&
        accountTypes[key]
      ) {
        balance +=
          balances.retirement.products.types[key].balance +
          balances.nonRetirement.products.types[key].balance;
        accountTypes[key].balance +=
          balances.retirement.products.types[key].balance +
          balances.nonRetirement.products.types[key].balance;
        accountTypes[key].userAccountNumbers = [
          ...balances.retirement.products.types[key].userAccountNumbers,
          ...balances.nonRetirement.products.types[key].userAccountNumbers
        ];
        accountNumbers.push(...accountTypes[key].userAccountNumbers);
      }
    }

    const percents = {
      brokerage: balance > 0 ? accountTypes['brokerage'].balance / balance : 0,
      collegeSavings:
        balance > 0 ? accountTypes['collegeSavings'].balance / balance : 0,
      mutualFund: balance > 0 ? accountTypes['mutualFund'].balance / balance : 0
    };

    return {
      balance,
      accountTypes,
      percents,
      accountNumbers
    };
  }
);

export const getTotalWithRpsBalances = createSelector(
  getRpsBalances,
  getAccountBalances,
  (rpsBalances, balances) => balances.aggregate + rpsBalances.aggregate
);

export const getTotalDailyChangeWithRpsBalances = createSelector(
  getActiveAccounts,
  getRpsBalances,
  (accounts, rpsBalances) => {
    const { accounts: rpsAccounts } = rpsBalances;
    const isCollegeSavingsOnly =
      accounts && every(accounts, (a) => isCollegeSavings(a.productType));
    if (rpsAccounts.length > 0 && isCollegeSavingsOnly) {
      return dailyChangeAmount(accounts);
    }
    const combined = concat(accounts, rpsAccounts);
    return dailyChangeAmount(combined);
  }
);

export const getTotalDailyRateWithRpsBalances = createSelector(
  getActiveAccounts,
  getRpsBalances,
  (accounts, rpsBalances) => {
    const isCollegeSavingsOnly =
      accounts && every(accounts, (a) => isCollegeSavings(a.productType));
    const { accounts: rpsAccounts } = rpsBalances;
    if (rpsAccounts.length > 0 && isCollegeSavingsOnly) {
      return dailyChangeRate(accounts);
    }
    const combined = concat(accounts, rpsAccounts);
    return dailyChangeRate(combined);
  }
);

export const getRpsYTDBalances = createSelector(
  getRpsBalances,
  getRpsYearEndBalance,
  (currentBalances, endOfYearBalance) => {
    if (currentBalances && endOfYearBalance) {
      const { aggregate: todaysTotal } = currentBalances;
      const yearlyChange = todaysTotal - endOfYearBalance;
      const yearlyRate =
        yearlyChange / (Number(endOfYearBalance) == 0 ? 1 : endOfYearBalance);

      return {
        rpsYearEnd: endOfYearBalance,
        rpsYearlyChange: yearlyChange,
        rpsYearlyRate: yearlyRate
      };
    }
    return null;
  }
);

/////// RETAIL BALANCES ///////

export const getCurrentRetailBalance = createSelector(
  getAccountBalances,
  (retailBalances) => {
    const { aggregate: todaysTotalBalance } = retailBalances;
    return todaysTotalBalance;
  }
);

export const getRetailYearEndBalance = createSelector(
  getAccountBalances,
  (retailBalances) => {
    const { beginningTotal } = retailBalances;
    return beginningTotal;
  }
);

export const getRetailCombinedYTDFigure = createSelector(
  getCurrentRetailBalance,
  getRetailYearEndBalance,
  (todaysTotalBalance, yearEndTotalBalance) => {
    return {
      todaysTotalBalance,
      yearEndTotalBalance
    };
  }
);

/////// RPS BALANCES ///////

export const getCurrentCrossoverClientBalance = createSelector(
  getCurrentRetailBalance,
  getRpsBalances,
  (retailBalance, rpsBalances) => {
    const { aggregate: rpsBalance } = rpsBalances;
    return rpsBalance + retailBalance;
  }
);

export const getYearEndCrossoverClientBalance = createSelector(
  getRpsYTDBalances,
  getRetailYearEndBalance,
  (rpsYTDBalances, retailYearEndBalance) => {
    if (!rpsYTDBalances) {
      return 0;
    }
    const { rpsYearEnd: rpsYearEndBalance } = rpsYTDBalances;
    return rpsYearEndBalance + retailYearEndBalance;
  }
);

export const getCombinedCrossoverYTDFigure = createSelector(
  getCurrentCrossoverClientBalance,
  getYearEndCrossoverClientBalance,
  (todaysTotalBalance, yearEndTotalBalance) => {
    return {
      todaysTotalBalance,
      yearEndTotalBalance
    };
  }
);

/////// YTD BALANCE ///////

export const getYearToDateAmount = createSelector(
  getRetailCombinedYTDFigure,
  getCombinedCrossoverYTDFigure,
  getRpsYTDBalances,
  (retailBalances, rpsBalances, rpsYTDBalances) => {
    const { todaysTotalBalance, yearEndTotalBalance } = rpsYTDBalances
      ? rpsBalances
      : retailBalances;
    return todaysTotalBalance - yearEndTotalBalance;
  }
);

/////// YTD RATE ///////

export const getYearToDateRate = createSelector(
  getRetailCombinedYTDFigure,
  getCombinedCrossoverYTDFigure,
  getRpsYTDBalances,
  (retailBalances, rpsBalances, rpsYTDBalances) => {
    const { todaysTotalBalance, yearEndTotalBalance } = rpsYTDBalances
      ? rpsBalances
      : retailBalances;
    return (
      (todaysTotalBalance - yearEndTotalBalance) / Math.abs(yearEndTotalBalance)
    );
  }
);
